// src/styles/analyticsResponsive.js
import { alpha } from '@mui/material/styles';

/**
 * Responsive styles for analytics components
 * This module provides consistent responsive styling for analytics pages
 */

/**
 * Get responsive grid configuration for KPI cards
 * @param {Object} theme - MUI theme
 * @returns {Object} Responsive grid props
 */
export const getKpiGridProps = (theme) => ({
  container: {
    spacing: { xs: 2, sm: 3 },
    mb: { xs: 2, sm: 3, md: 4 },
  },
  item: {
    xs: 6,
    sm: 6,
    md: 3,
  },
});

/**
 * Get responsive grid configuration for chart cards
 * @param {Object} theme - MUI theme
 * @param {string} size - Chart size (small, medium, large, full)
 * @returns {Object} Responsive grid props
 */
export const getChartGridProps = (theme, size = 'medium') => {
  const sizes = {
    small: {
      xs: 12,
      sm: 6,
      md: 4,
      lg: 4,
    },
    medium: {
      xs: 12,
      sm: 12,
      md: 6,
      lg: 6,
    },
    large: {
      xs: 12,
      sm: 12,
      md: 8,
      lg: 8,
    },
    full: {
      xs: 12,
    },
  };
  
  return sizes[size] || sizes.medium;
};

/**
 * Get responsive styles for page header
 * @param {Object} theme - MUI theme
 * @returns {Object} Styles object
 */
export const getPageHeaderStyles = (theme) => ({
  mb: { xs: 2, sm: 3, md: 4 },
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  justifyContent: 'space-between',
  alignItems: { xs: 'flex-start', sm: 'center' },
  gap: { xs: 2, sm: 2 },
});

/**
 * Get responsive styles for page filters
 * @param {Object} theme - MUI theme
 * @returns {Object} Styles object
 */
export const getFilterBarStyles = (theme) => ({
  mb: { xs: 2, sm: 3, md: 4 },
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  justifyContent: 'space-between',
  alignItems: { xs: 'flex-start', sm: 'flex-end' },
  gap: 2,
});

/**
 * Get responsive styles for tabs container
 * @param {Object} theme - MUI theme
 * @returns {Object} Styles object
 */
export const getTabsContainerStyles = (theme) => ({
  mb: { xs: 2, sm: 3 },
  borderBottom: `1px solid ${theme.palette.divider}`,
  overflowX: 'auto',
  '& .MuiTabs-root': {
    minWidth: 'min-content',
  },
});

/**
 * Get responsive styles for chart heights based on screen size
 * @param {Object} theme - MUI theme
 * @param {Object} options - Configuration options
 * @returns {Object} Height configuration object
 */
export const getResponsiveChartHeight = (theme, options = {}) => {
  const {
    baseHeight = 400,
    smallScreenReduction = 50,
    mobileReduction = 100,
  } = options;
  
  return {
    height: {
      xs: baseHeight - mobileReduction,
      sm: baseHeight - smallScreenReduction,
      md: baseHeight,
    },
  };
};

/**
 * Get responsive styles for mobile-optimized cards
 * @param {Object} theme - MUI theme
 * @returns {Object} Styles object
 */
export const getMobileCardStyles = (theme) => ({
  p: { xs: 1.5, sm: 2, md: 3 },
  '& .chart-container': {
    marginLeft: { xs: -1, sm: -1, md: 0 },
    marginRight: { xs: -1, sm: -1, md: 0 },
  },
});

/**
 * Get responsive styles for data grid/tables
 * @param {Object} theme - MUI theme
 * @returns {Object} Styles object
 */
export const getResponsiveTableStyles = (theme) => ({
  '& .MuiTableContainer-root': {
    maxHeight: {
      xs: 300,
      sm: 350,
      md: 400,
    },
  },
  '& .MuiTableCell-root': {
    padding: {
      xs: '8px 12px',
      sm: '12px 16px',
    },
  },
});

/**
 * Get responsive spacing utility
 * @param {Object} theme - MUI theme
 * @returns {Function} Function that returns responsive spacing values
 */
export const responsiveSpacing = (theme) => (desktop, tablet = null, mobile = null) => ({
  ...(mobile !== null ? { xs: theme.spacing(mobile) } : {}),
  ...(tablet !== null ? { sm: theme.spacing(tablet) } : {}),
  md: theme.spacing(desktop)
});

/**
 * Get responsive typography styles
 * @param {Object} theme - MUI theme
 * @returns {Object} Typography style overrides for analytics components
 */
export const getResponsiveTypography = (theme) => ({
  h1: {
    fontSize: {
      xs: '1.5rem',
      sm: '2rem',
      md: '2.5rem',
    },
  },
  h2: {
    fontSize: {
      xs: '1.25rem',
      sm: '1.75rem',
      md: '2rem',
    },
  },
  h3: {
    fontSize: {
      xs: '1.15rem',
      sm: '1.5rem',
      md: '1.75rem',
    },
  },
  h4: {
    fontSize: {
      xs: '1rem',
      sm: '1.25rem',
      md: '1.5rem',
    },
  },
  subtitle1: {
    fontSize: {
      xs: '0.95rem',
      sm: '1rem',
      md: '1.1rem',
    },
  },
});